import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { inventoryService } from "../services/database";
import AlertModal, { AlertIcons } from "../components/AlertModal";
import type { InventoryItem } from "../types/database";

interface ScannedItem {
  asset: any; // Using any for now since getAllAssets returns a different structure
  scannedAt: Date;
  status: "checked_in" | "already_available" | "error";
  checkoutRecord?: any;
}

interface AssetInfoProps {
  asset: any; // Using any for now since getAllAssets returns a different structure
  inventoryItem: InventoryItem | null;
  checkoutRecord?: any;
}

function AssetInfo({ asset, inventoryItem, checkoutRecord }: AssetInfoProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      <div className="flex items-start space-x-4">
        {/* Asset Image */}
        <div className="flex-shrink-0">
          {inventoryItem?.images && inventoryItem.images.length > 0 ? (
            <img
              src={inventoryItem.images[0]}
              alt={inventoryItem.name}
              className="h-16 w-16 object-cover rounded-lg border border-gray-200"
            />
          ) : (
            <div className="h-16 w-16 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center">
              <svg
                className="h-8 w-8 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Asset Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              {inventoryItem?.name || "Unknown Item"}
            </h3>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                asset.status === "checked_out"
                  ? "bg-red-100 text-red-800"
                  : asset.status === "available"
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {asset.status.replace("_", " ").toUpperCase()}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Barcode:</span>
              <span className="ml-2 font-mono text-gray-900">
                {asset.barcode}
              </span>
            </div>
            {asset.serial_number && (
              <div>
                <span className="text-gray-500">Serial:</span>
                <span className="ml-2 text-gray-900">
                  {asset.serial_number}
                </span>
              </div>
            )}
            <div>
              <span className="text-gray-500">Category:</span>
              <span className="ml-2 text-gray-900">
                {inventoryItem?.category || "Unknown"}
              </span>
            </div>
            {checkoutRecord && (
              <div>
                <span className="text-gray-500">Checked Out:</span>
                <span className="ml-2 text-gray-900">
                  {new Date(checkoutRecord.checkout_date).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>

          {checkoutRecord && checkoutRecord.job && (
            <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
              <span className="text-sm text-blue-700">
                <strong>Job ID:</strong> {checkoutRecord.job}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CheckIn() {
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();
  const [barcodeInput, setBarcodeInput] = useState("");
  const [scannedItems, setScannedItems] = useState<ScannedItem[]>([]);

  // Alert modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "warning" | "info";
    icon?: React.ReactNode;
    showCancel?: boolean;
    onConfirm?: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    showCancel: false,
  });

  // Fetch all inventory items to get names and images
  const { data: inventoryItems = [] } = useQuery<InventoryItem[]>({
    queryKey: ["inventory", currentOrganization?.id],
    queryFn: () => inventoryService.getInventoryItems(currentOrganization?.id),
    enabled: !!currentOrganization?.id,
  });

  // Fetch all assets
  const { data: assets = [] } = useQuery({
    queryKey: ["assets", currentOrganization?.id],
    queryFn: () => inventoryService.getAllAssets(currentOrganization?.id),
    enabled: !!currentOrganization?.id,
  });

  // Helper function to show alerts
  const showAlert = (
    title: string,
    message: string,
    type: "success" | "error" | "warning" | "info" = "info",
    icon?: React.ReactNode,
    showCancel = false,
    onConfirm?: () => void
  ) => {
    setAlertModal({
      isOpen: true,
      title,
      message,
      type,
      icon,
      showCancel,
      onConfirm,
    });
  };

  const closeAlert = () => {
    setAlertModal((prev) => ({ ...prev, isOpen: false }));
  };

  // Mutation for updating asset status
  const updateAssetMutation = useMutation({
    mutationFn: async ({
      assetId,
      updates,
    }: {
      assetId: string;
      updates: any;
    }) => {
      return inventoryService.updateAsset(
        assetId,
        updates,
        currentOrganization?.id || ""
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
    },
  });

  // Mutation for updating checkout record
  const updateCheckoutRecordMutation = useMutation({
    mutationFn: async (checkoutRecordId: string) => {
      return inventoryService.returnAsset(
        checkoutRecordId,
        currentOrganization?.id || ""
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
    },
  });

  // Function to check in an asset
  const checkInAsset = async (asset: any) => {
    try {
      // Get the most recent checkout record for this asset
      const checkoutRecords = await inventoryService.getCheckoutRecords(
        asset.id,
        currentOrganization?.id || ""
      );

      const activeCheckoutRecord = checkoutRecords.find(
        (record) => !record.return_date
      );

      if (!activeCheckoutRecord) {
        showAlert(
          "No Active Checkout",
          `Asset ${asset.barcode} is not currently checked out.`,
          "warning",
          AlertIcons.warning
        );
        return false;
      }

      // Update asset status to available
      await updateAssetMutation.mutateAsync({
        assetId: asset.id,
        updates: { status: "available" },
      });

      // Update checkout record with return date
      await updateCheckoutRecordMutation.mutateAsync(activeCheckoutRecord.id);

      console.log(`Asset ${asset.barcode} checked in successfully`);
      return true;
    } catch (error) {
      console.error("Error checking in asset:", error);
      showAlert(
        "Check-in Failed",
        "Failed to check in asset. Please try again.",
        "error",
        AlertIcons.error
      );
      return false;
    }
  };

  // Handle barcode scan/input
  const handleBarcodeSubmit = async (barcode: string) => {
    if (!barcode.trim()) {
      return;
    }

    // Find the asset by barcode
    const asset = assets.find((a) => a.barcode === barcode);

    if (!asset) {
      showAlert(
        "Asset Not Found",
        `No asset found with barcode: ${barcode}`,
        "error",
        AlertIcons.error
      );
      setBarcodeInput("");
      return;
    }

    // Check if asset is already scanned in current session
    const existingScannedItem = scannedItems.find(
      (item) => item.asset.barcode === barcode
    );

    if (existingScannedItem) {
      showAlert(
        "Asset Already Scanned",
        `Asset ${barcode} has already been scanned in this session.`,
        "warning",
        AlertIcons.warning
      );
      setBarcodeInput("");
      return;
    }

    // Check if asset is already available
    if (asset.status === "available") {
      showAlert(
        "Asset Already Available",
        `Asset ${barcode} is already available and doesn't need to be checked in.`,
        "warning",
        AlertIcons.warning
      );
      setBarcodeInput("");
      return;
    }

    // Get checkout record for display
    let checkoutRecord = null;
    try {
      const checkoutRecords = await inventoryService.getCheckoutRecords(
        asset.id,
        currentOrganization?.id || ""
      );
      checkoutRecord = checkoutRecords.find((record) => !record.return_date);
    } catch (error) {
      console.error("Error fetching checkout records:", error);
    }

    // Add to scanned items
    const newScannedItem: ScannedItem = {
      asset,
      scannedAt: new Date(),
      status: "checked_in",
      checkoutRecord,
    };

    setScannedItems((prev) => [...prev, newScannedItem]);
    setBarcodeInput("");

    // Automatically check in the asset
    const success = await checkInAsset(asset);
    if (success) {
      // Update the scanned item status
      setScannedItems((prev) =>
        prev.map((item) =>
          item.asset.id === asset.id
            ? { ...item, status: "checked_in" as const }
            : item
        )
      );
    } else {
      // Update the scanned item status to error
      setScannedItems((prev) =>
        prev.map((item) =>
          item.asset.id === asset.id
            ? { ...item, status: "error" as const }
            : item
        )
      );
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleBarcodeSubmit(barcodeInput);
  };

  // Handle keyboard input for barcode scanner
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "Enter" && barcodeInput.trim()) {
        handleBarcodeSubmit(barcodeInput);
      }
    };

    document.addEventListener("keypress", handleKeyPress);
    return () => document.removeEventListener("keypress", handleKeyPress);
  }, [barcodeInput]);

  const clearScannedItems = () => {
    setScannedItems([]);
  };

  const getInventoryItem = (asset: any) => {
    return (
      inventoryItems.find((item) => item.id === asset.parent_item_id) || null
    );
  };

  const checkedInCount = scannedItems.filter(
    (item) => item.status === "checked_in"
  ).length;

  const errorCount = scannedItems.filter(
    (item) => item.status === "error"
  ).length;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Check In Assets
        </h1>
        <p className="text-gray-600">
          Scan barcodes to check in assets and return them to available status
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-8 w-8 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Checked In</p>
              <p className="text-2xl font-semibold text-gray-900">
                {checkedInCount}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-8 w-8 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Errors</p>
              <p className="text-2xl font-semibold text-gray-900">
                {errorCount}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-8 w-8 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Scanned</p>
              <p className="text-2xl font-semibold text-gray-900">
                {scannedItems.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Barcode Input */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">
          Scan Asset Barcode
        </h2>
        <form onSubmit={handleSubmit} className="flex gap-4">
          <input
            type="text"
            value={barcodeInput}
            onChange={(e) => setBarcodeInput(e.target.value)}
            placeholder="Scan or enter barcode..."
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-lg font-mono"
            autoFocus
          />
          <button
            type="submit"
            disabled={!barcodeInput.trim()}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Check In
          </button>
        </form>
        <p className="text-sm text-gray-500 mt-2">
          Press Enter or click Check In to process the barcode
        </p>
      </div>

      {/* Scanned Items */}
      {scannedItems.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Scanned Assets ({scannedItems.length})
            </h2>
            <button
              onClick={clearScannedItems}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear All
            </button>
          </div>

          <div className="space-y-4">
            {scannedItems.map((item, index) => (
              <div
                key={`${item.asset.id}-${index}`}
                className={`border-l-4 ${
                  item.status === "checked_in"
                    ? "border-green-500 bg-green-50"
                    : item.status === "error"
                    ? "border-red-500 bg-red-50"
                    : "border-gray-300 bg-gray-50"
                }`}
              >
                <AssetInfo
                  asset={item.asset}
                  inventoryItem={getInventoryItem(item.asset)}
                  checkoutRecord={item.checkoutRecord}
                />
                <div className="px-4 pb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">
                      Scanned at: {item.scannedAt.toLocaleTimeString()}
                    </span>
                    <span
                      className={`font-medium ${
                        item.status === "checked_in"
                          ? "text-green-700"
                          : item.status === "error"
                          ? "text-red-700"
                          : "text-gray-700"
                      }`}
                    >
                      {item.status === "checked_in"
                        ? "✓ Checked In"
                        : item.status === "error"
                        ? "✗ Error"
                        : "Processing..."}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        icon={alertModal.icon}
        showCancel={alertModal.showCancel}
        onConfirm={alertModal.onConfirm}
        onClose={closeAlert}
      />
    </div>
  );
}
